import { useEffect, useMemo, useState } from "react";
import { Modal } from "react-bootstrap";

const matches = (log, keyword) => {
  if (!keyword) return true;
  const text = [log.code, log.orderCode, log.orderName, log.orderCustomerCode, log.orderCustomerName]
    .filter(Boolean)
    .join(" ")
    .toLowerCase();
  return text.includes(keyword);
};

/** Popup chỉnh sửa danh sách mã đang chờ xác nhận: tìm kiếm để lọc, bỏ từng mã hoặc bỏ các mã đã chọn. */
const PendingCodesModal = ({ show, onHide, pendingLogs, onRemove }) => {
  const [keyword, setKeyword] = useState("");
  const [selected, setSelected] = useState([]);
  const [removing, setRemoving] = useState(false);

  useEffect(() => {
    if (!show) {
      setKeyword("");
      setSelected([]);
    }
  }, [show]);

  // Bỏ chọn các mã không còn trong danh sách (đã bỏ / đã xác nhận).
  useEffect(() => {
    setSelected((prev) => prev.filter((id) => pendingLogs.some((log) => log.id === id)));
  }, [pendingLogs]);

  const normalized = keyword.trim().toLowerCase();
  const rows = useMemo(() => pendingLogs.filter((log) => matches(log, normalized)), [pendingLogs, normalized]);
  const allSelected = rows.length > 0 && rows.every((log) => selected.includes(log.id));

  const toggle = (id) => setSelected((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));
  const toggleAll = () =>
    setSelected((prev) =>
      allSelected ? prev.filter((id) => !rows.some((log) => log.id === id)) : [...new Set([...prev, ...rows.map((log) => log.id)])]
    );

  const removeLogs = async (logs) => {
    setRemoving(true);
    try {
      for (const log of logs) {
        // Tuần tự để server ghi lịch sử theo đúng thứ tự.
        await onRemove(log);
      }
    } finally {
      setRemoving(false);
    }
  };

  const removeSelected = () => removeLogs(pendingLogs.filter((log) => selected.includes(log.id)));

  return (
    <Modal show={show} onHide={onHide} size="lg" scrollable className="warehouse-modal">
      <Modal.Header closeButton>
        <Modal.Title as="h5">Chỉnh sửa mã chờ xác nhận ({pendingLogs.length})</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <input
          type="search"
          className="form-control mb-2"
          placeholder="Tìm theo mã vận đơn, mã đơn, khách hàng..."
          value={keyword}
          onChange={(e) => setKeyword(e.target.value)}
          autoFocus
        />
        <div className="table-responsive">
          <table className="table table-sm table-hover align-middle mb-0">
            <thead className="table-light">
              <tr>
                <th style={{ width: 36 }}>
                  <input type="checkbox" checked={allSelected} onChange={toggleAll} disabled={rows.length === 0 || removing} aria-label="Chọn tất cả" />
                </th>
                <th>Mã vận đơn</th>
                <th>Đơn hàng</th>
                <th>Khách hàng</th>
                <th>Trạng thái hiện tại</th>
                <th style={{ width: 40 }}></th>
              </tr>
            </thead>
            <tbody>
              {rows.length === 0 && (
                <tr>
                  <td colSpan={6} className="text-center text-muted py-4">
                    {pendingLogs.length === 0 ? "Không có mã nào đang chờ xác nhận" : "Không có mã khớp tìm kiếm"}
                  </td>
                </tr>
              )}
              {rows.map((log) => (
                <tr key={log.id}>
                  <td>
                    <input type="checkbox" checked={selected.includes(log.id)} onChange={() => toggle(log.id)} disabled={removing} aria-label={`Chọn ${log.code}`} />
                  </td>
                  <td className="fw-semibold text-nowrap">{log.code}</td>
                  <td className="text-nowrap">
                    {log.orderCode || "-"}
                    {log.orderName && <div className="small text-muted text-truncate" style={{ maxWidth: 200 }}>{log.orderName}</div>}
                  </td>
                  <td className="text-nowrap">{log.orderCustomerCode || "-"}</td>
                  <td className="text-nowrap">
                    {log.statusBeforeName ? (
                      <span className="warehouse-status-chip" style={{ background: log.statusBeforeColor || "#eee" }}>{log.statusBeforeName}</span>
                    ) : "-"}
                  </td>
                  <td>
                    <button
                      type="button"
                      className="btn btn-sm btn-link text-danger p-0"
                      onClick={() => removeLogs([log])}
                      disabled={removing}
                      title="Bỏ mã khỏi danh sách chờ"
                      aria-label={`Bỏ mã ${log.code}`}
                    >
                      ✕
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Modal.Body>
      <Modal.Footer className="justify-content-between">
        <button type="button" className="btn btn-outline-danger btn-sm" onClick={removeSelected} disabled={selected.length === 0 || removing}>
          {removing ? "Đang bỏ..." : `Bỏ ${selected.length} mã đã chọn`}
        </button>
        <button type="button" className="btn btn-primary btn-sm" onClick={onHide}>
          Xong
        </button>
      </Modal.Footer>
    </Modal>
  );
};

export default PendingCodesModal;
